import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiGet } from '../utils/api';
import styles from './FriendsPage.module.css';

const FriendsPage = () => {
  const { user } = useAuth();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        setLoading(true);
        const response = await apiGet('/friendships/friends');
        
        if (response.ok) {
          const friendsData = await response.json();
          // Transform the API response to match our component structure
          const transformedFriends = friendsData.map(friendship => ({
            id: friendship.friend.id,
            name: `${friendship.friend.first_name} ${friendship.friend.last_name}`,
            email: friendship.friend.email,
            firstName: friendship.friend.first_name,
            lastName: friendship.friend.last_name,
            acceptedAt: friendship.accepted_at,
            status: friendship.status
          }));
          
          setFriends(transformedFriends);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to load friends');
          setFriends([]);
        }
      } catch (error) {
        console.error('Error fetching friends:', error);
        setError('Network error. Please check your connection and try again.');
        setFriends([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFriends();
  }, [user]);

  if (loading) {
    return (
      <div className={styles.friendsPage}>
        <h1>Friends</h1>
        <div className="loading">Loading friends...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.friendsPage}>
        <header className={styles.friendsHeader}>
          <h1>Your Friends</h1>
          <p>Connect and share podcasts with your friends</p>
        </header>
        <div className="error-state">
          <h2>Error Loading Friends</h2>
          <p>{error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.friendsPage}>
      <header className={styles.friendsHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h1>Your Friends</h1>
            <p>Connect and share podcasts with your friends</p>
          </div>
          <div className={styles.headerActions}>
            <Link to="/friend-requests" className={styles.requestsButton}>
              Friend Requests
            </Link>
            <Link to="/explore-users" className={styles.exploreButton}>
              Explore Users
            </Link>
          </div>
        </div>
      </header>
      
      <div className={styles.friendsList}>
        {friends.length === 0 ? (
          <div className="empty-state">
            <h3>No friends. But lucky for you there are only ballers on this platform so start making some friends.</h3>
          </div>
        ) : (
          friends.map(friend => (
            <div key={friend.id} className={styles.friendCard}>
              <div className={styles.friendInfo}>
                <h3>{friend.name}</h3>
                <p>{friend.email}</p>
                <span className={styles.lastActive}>
                  Friends since: {new Date(friend.acceptedAt).toLocaleDateString()}
                </span>
              </div>
              <div className={styles.friendActions}>
                <Link 
                  to={`/user/${friend.id}`}
                  className="btn btn-secondary"
                >
                  View Profile
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FriendsPage;
